import { gql, useLazyQuery, useMutation } from '@apollo/client';
import AccessSection from '@components/side-card/basic/AccessSection';
import DescriptionSection from '@components/side-card/basic/DescriptionSection';
import ServiceSection from '@components/side-card/basic/ServiceSection';
import ExtensionIcon from '@mui/icons-material/Extension';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import Typography from '@mui/material/Typography';
import { Suspense, useState } from 'react';

import HumanReadableProperty from '@components/side-card/geo-timer/HumanReadableProperty';
import ParametersTagsModal from '@components/side-card/reaction-button/modals/ParameterTagsModal';
import tabStyle from '@components/side-card/tab.module.css';
import EditIcon from '@mui/icons-material/Edit';
import { ListItemSecondaryAction } from '@mui/material';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import ParameterObjectModal from './modals/ParameterObjectModal';

const GET_OBJECT_BY_ID = gql`
  query getObject($id: UUID!) {
    object(id: $id) {
      id
      name
    }
  }
`;

const UPDATE_PROPERTY = gql`
  mutation updateObjectProperty($input: UpdateObjectPropertyInput!) {
    updateObjectProperty(input: $input) {
      clientMutationId
    }
  }
`;

const GET_OBJECT_PROPERTY = gql`
  query getObjectProperty($id: UUID!) {
    objectProperty(id: $id) {
      id
      spec {
        id
        description
      }
      property
      object {
        id
        name
        schemaTags
      }
    }
  }
`;

const ReactionButtonGeneralTab = ({ item }) => {
  const [loadObjectProperty, { loading: loadingProperty }] = useLazyQuery(GET_OBJECT_PROPERTY);
  // const [loadDevicePropertyLinkedData, { loading: loadingDevicePropertyData }] = useLazyQuery(DEVICE_PROP_LINKED_QUERY);
  const [objectName, setObjectName] = useState('');
  const [propertyName, setPropertyName] = useState('');
  const isLoading = false;
  const [updateProperty] = useMutation(UPDATE_PROPERTY);

  const currentIcon = () => {
    return item.objectProperties.find((item) => item.key === 'valueCurrentIcon');
  };

  const settingsObject = () => {
    return item.objectProperties.find((item) => item.key === 'settingsObject');
  };

  const settingsTags = () => {
    return item.objectProperties.find((item) => item.key === 'settingsTags');
  };

  const currentIconName = () => {
    const filtersGroup = currentIcon().value;

    return {
      query: GET_OBJECT_BY_ID,
      config: {
        variables: {
          id: filtersGroup,
        },
      },
    };
  };

  const getLoadedValue = (loader, value) => {
    if (loader) {
      return 'Loading...';
    }
    if (!loader && value) {
      return value;
    }
    return 'n/a';
  };

  const parametersGeotag = () => {
    return {
      query: GET_OBJECT_BY_ID,
      config: {
        variables: {
          id: settingsObject().value,
        },
      },
    };
  };

  return (
    <>
      <List>
        <ListSubheader color="primary" className={tabStyle.listSubheader}>
          <Typography variant="subtitle2">Target object</Typography>
        </ListSubheader>

        <ListItem
          classes={{
            container: tabStyle.itemToHover,
          }}
          style={{ height: '48px', paddingRight: '96px' }}
        >
          <ListItemIcon>
            <ExtensionIcon></ExtensionIcon>
          </ListItemIcon>
          <ListItemText
            primary={
              <Typography variant="body1">
                Object:{' '}
                {settingsObject().value && (
                  <>
                    <Suspense fallback={<span>Loading...</span>}>
                      <HumanReadableProperty
                        payload={parametersGeotag()}
                        getValue={(data) => data?.object?.name || 'n/a'}
                      ></HumanReadableProperty>
                    </Suspense>
                  </>
                )}
                {!settingsObject().value && <span> n/a</span>}
              </Typography>
            }
          />
          <ListItemSecondaryAction className={tabStyle.itemToHideOrShow}>
            <IconButton
              size="small"
              onClick={() => {
                ParameterObjectModal({
                  id: item.id,
                  settingsLinkedOnly: false,
                  linkedObjects: [],
                  schemaId: null,
                  objectId: settingsObject()?.value || null,
                  save: (object) => {
                    updateProperty({
                      variables: {
                        input: {
                          id: settingsObject().id,
                          patch: {
                            value: object.id || null,
                          },
                        },
                      },
                    }).then();
                  },
                })
                  .then()
                  .catch(() => {});
              }}
            >
              <EditIcon />
            </IconButton>
          </ListItemSecondaryAction>
        </ListItem>

        <ListSubheader color="primary" className={tabStyle.listSubheader}>
          <Typography variant="subtitle2">Tags</Typography>
        </ListSubheader>

        <ListItem
          classes={{
            container: tabStyle.itemToHover,
          }}
          style={{ height: '48px', paddingRight: '96px' }}
        >
          {settingsTags().value.map((tag, index) => (
            <Chip sx={{ marginRight: '6px' }} key={`tagItem-${index}`} label={tag} color="primary" />
          ))}
          {settingsTags().value.length === 0 && 'No tags'}
          <ListItemSecondaryAction className={tabStyle.itemToHideOrShow}>
            <IconButton
              size="small"
              onClick={() => {
                ParametersTagsModal({
                  settingsTags: settingsTags().value,
                  objectId: settingsTags()?.value || null,
                  save: (tags) => {
                    updateProperty({
                      variables: {
                        input: {
                          id: settingsTags().id,
                          patch: {
                            value: tags || [],
                          },
                        },
                      },
                    }).then();
                  },
                })
                  .then()
                  .catch(() => {});
              }}
            >
              <EditIcon />
            </IconButton>
          </ListItemSecondaryAction>
        </ListItem>

        <ServiceSection classes={tabStyle} item={item} />

        <AccessSection classes={tabStyle} item={item} />

        {item.description && <DescriptionSection classes={tabStyle} item={item} />}
      </List>
    </>
  );
};

export default ReactionButtonGeneralTab;
